/* eslint-disable import/no-dynamic-require */
/* eslint-disable @typescript-eslint/no-var-requires */
import React, { ReactElement, useEffect, useMemo, useState, useRef } from 'react';

import { Col, Divider, Layout, Row, TypographyText } from '@components/atoms';
import { SelectType, SurveyCategory } from '@constants/enum';
import { useReduxForm } from '@hooks/useReduxForm';
import { getSurveyObj } from '@utils/surveyUtils';
import _ from 'lodash';

import Check from './Check';
import Date from './Date';
import NumberInput from './NumberInput';
import Radio from './Radio';

function DetailSurvey(): ReactElement {
  const ImgCodeMapper = useMemo(
    () =>
      new Map([
        ['a', { image: 'digestion', label: '소화', src: `${process.env.PUBLIC_URL}/img/digestion4x.webp` }],
        ['b', { image: 'pee', label: '소변', src: `${process.env.PUBLIC_URL}/img/pee4x.webp` }],
        ['c', { image: 'poop', label: '대변', src: `${process.env.PUBLIC_URL}/img/poop4x.webp` }],
        ['e', { image: 'hanyeol', label: '한열', src: `${process.env.PUBLIC_URL}/img/hanyeol.webp` }],
        ['g', { image: 'emotion', label: '감정/정신', src: `${process.env.PUBLIC_URL}/img/emotion4x.webp` }],
        ['h', { image: 'sleep', label: '수면', src: `${process.env.PUBLIC_URL}/img/sleep4x.webp` }],
        ['j', { image: 'birth', label: '임신/출산', src: `${process.env.PUBLIC_URL}/img/birth4x.webp` }],
        ['k', { image: 'menses', label: '월경(생리)', src: `${process.env.PUBLIC_URL}/img/menses4x.webp` }],
        [
          'A',
          { image: 'tiredness', label: '피로/면역력', src: `${process.env.PUBLIC_URL}/img/tiredness4x.webp` },
        ],
      ]),
    [],
  );

  const imgCodeArr = useMemo(() => Array.from(ImgCodeMapper, ([key]) => key), []);

  const imageComponentsArr = useMemo(
    () =>
      _.map(
        Array.from(ImgCodeMapper, ([key, value]) => ({ key, ...value })),
        (e) => {
          const img = new Image();
          img.src = e.src;
          const imgComp = React.createElement('img', { src: e.src, width: '172px', alt: e.image });
          return imgComp;
        },
      ),
    [],
  );

  const { formData, setFormData } = useReduxForm('survey');
  const { currentSurvey, visitedSurvey = [], mode } = formData;
  const [survey, setSurvey] = useState(getSurveyObj(currentSurvey ?? 'a'));
  const imgFlag = survey.category === SurveyCategory.Main && ImgCodeMapper.get(currentSurvey)?.image;

  useEffect(() => {
    const surObj = getSurveyObj(currentSurvey ?? 'a');
    setSurvey(surObj);
    const body = { visitedSurvey: _.uniq(_.compact([...visitedSurvey, currentSurvey])) };
    setFormData(body);
  }, [currentSurvey]);

  const scrollAreaRef = useRef(null);

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0;
    }
  }, [currentSurvey]);

  return (
    <div
      ref={scrollAreaRef}
      style={{
        background: '#fff',
        height: '100dvh',
        overflow: 'auto',
        width: 'calc(100% + 24px)',
        maxWidth: '480px',
        maxHeight: '1000px',
        transform: 'translateX(24px)',
        paddingRight: '24px',
        marginLeft: '-24px',
      }}
    >
      <Row>
        <Col yzTypeCol="center">
          <TypographyText yzTypeTypographyText="title">{survey.label}</TypographyText>
        </Col>
      </Row>
      <Divider margin={7.5} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
      {survey?.desc?.map((e: string) => {
        if (e.startsWith('<')) {
          return (
            <>
              <Row>
                <Col yzTypeCol="center">
                  <TypographyText yzTypeTypographyText="symptom">{e}</TypographyText>
                </Col>
              </Row>
              <Divider margin={3} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
            </>
          );
        }
        return (
          <Row>
            <Col yzTypeCol="center">
              <TypographyText yzTypeTypographyText="basic">{e}</TypographyText>
            </Col>
          </Row>
        );
      })}
      {imgFlag && (
        <>
          <Divider margin={11} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
          <Row>
            <Col style={{ width: '172px', height: '130px' }} yzTypeCol="center">
              {imageComponentsArr[imgCodeArr.findIndex((e) => e === currentSurvey)]}
            </Col>
          </Row>
          <Divider margin={-5} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
        </>
      )}
      {survey.selectType === SelectType.Radio && (
        <>
          <Divider margin={8} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
          <Radio answers={survey.answers ?? []} formKey={survey?.formKey} />
        </>
      )}
      {survey.selectType === SelectType.Check && (
        <>
          <Divider margin={8} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
          <Check answers={survey.answers ?? []} formKey={survey?.formKey} />
        </>
      )}
      {[SelectType.NumberInput].includes(survey.selectType) && <NumberInput formKey={survey?.formKey} />}
      {[SelectType.Date].includes(survey.selectType) && <Date formKey={survey?.formKey} />}
      <Divider margin={42} yzTypeDivider="TRANSPARENT_LITTLE_SPACE" />
    </div>
  );
}

export default DetailSurvey;
